/* CitationChip and SourcesPanel (rules 21, 22; spec 6.4). Sources strip after Perplexica (MIT);
   passage highlight with relevance after Kotaemon (Apache-2.0). Chips are neutral, not accent. */
import { useId, useMemo, useState } from 'react';
import { ExternalLink, TriangleAlert } from 'lucide-react';
import type { Citation } from '../lib/api-types';
import { Chip } from './basics';
import { Tabs } from './Tabs';
import { cx } from './cx';

const initial = (c: Citation) => (c.domain ?? c.title).replace(/^www\./, '').charAt(0).toUpperCase();

export function CitationChip({ citation, onOpen }: { citation: Citation; onOpen?: (c: Citation) => void }) {
  const [show, setShow] = useState(false);
  const id = useId();
  const n = citation.n ?? '•';
  return (
    <span className="cite-w" onPointerEnter={() => setShow(true)} onPointerLeave={() => setShow(false)}>
      <button
        type="button"
        className={cx('cite')}
        aria-label={`Source ${n}: ${citation.domain ?? citation.title}`}
        aria-describedby={show ? id : undefined}
        aria-expanded={show}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') setShow(false); }}
        onClick={() => onOpen?.(citation)}
        data-cap="SEARCH-01"
      >
        {n}
      </button>
      {show && (
        <span className="cite-pop" role="tooltip" id={id}>
          <span className="row"><span className="fav" aria-hidden="true">{initial(citation)}</span><b>{citation.title}</b></span>
          <small className="muted">{[citation.domain, citation.date, citation.type].filter(Boolean).join(' · ')}</small>
          {citation.excerpt && <q>{citation.excerpt}</q>}
          {citation.relevance !== undefined && <small className="muted">Relevance {citation.relevance.toFixed(2)} · click to open the passage</small>}
        </span>
      )}
    </span>
  );
}

/** Quiet text links under an answer: the first cited sources by name, then the cited/consulted counts. */
export function SourceStrip({ citations, onOpen, onOpenAll }: { citations: Citation[]; onOpen?: (c: Citation) => void; onOpenAll?: () => void }) {
  const cited = citations.filter((c) => c.cited !== false);
  if (!citations.length) return null;
  return (
    <div className="srcstrip" role="group" aria-label="Sources">
      <span className="muted">Sources:</span>
      {cited.slice(0, 3).map((c) => (
        <button key={c.id} type="button" className="tl" onClick={() => onOpen?.(c)} title={c.title}>{c.domain ?? c.title}</button>
      ))}
      <button type="button" className="tl" onClick={onOpenAll}>All {cited.length} cited{citations.length > cited.length ? `, ${citations.length} consulted` : ''}</button>
    </div>
  );
}

export interface SourcesPanelProps {
  citations: Citation[];
  selectedId?: string;
  onSelect?: (c: Citation) => void;
  tab?: 'cited' | 'consulted';
  onTabChange?: (t: 'cited' | 'consulted') => void;
}

export function SourcesPanel({ citations, selectedId, onSelect, tab: tabProp, onTabChange }: SourcesPanelProps) {
  const [tabS, setTabS] = useState<'cited' | 'consulted'>('cited');
  const [typeFilter, setTypeFilter] = useState('All');
  const [selS, setSelS] = useState<string | undefined>();
  const tab = tabProp ?? tabS;
  const cited = citations.filter((c) => c.cited !== false);
  const rows = (tab === 'cited' ? cited : citations).filter((c) => typeFilter === 'All' || c.type === typeFilter);
  const types = useMemo(() => ['All', ...Array.from(new Set(citations.map((c) => c.type).filter(Boolean) as string[]))], [citations]);
  const selId = selectedId ?? selS ?? rows[0]?.id;
  const sel = citations.find((c) => c.id === selId);
  const pick = (c: Citation) => { setSelS(c.id); onSelect?.(c); };
  const setTab = (t: string) => { setTabS(t as 'cited' | 'consulted'); onTabChange?.(t as 'cited' | 'consulted'); };
  return (
    <div className="srcp" data-cap="SEARCH-04">
      <Tabs bare label="Sources" value={tab} onChange={setTab} tabs={[{ id: 'cited', label: `Cited (${cited.length})` }, { id: 'consulted', label: `Consulted (${citations.length})` }]} />
      {types.length > 2 && (
        <div className="pill-row" role="group" aria-label="Filter by type">
          {types.map((t) => <button key={t} type="button" className="pill" aria-pressed={typeFilter === t} onClick={() => setTypeFilter(t)}>{t}</button>)}
        </div>
      )}
      <ul className="srclist">
        {rows.map((c) => (
          <li key={c.id}>
            <button type="button" className="srcrow" aria-current={c.id === selId || undefined} onClick={() => pick(c)}>
              <span className="fav" aria-hidden="true">{initial(c)}</span>
              <span className="srcmeta"><b>{c.title}</b><small>{[c.domain, c.date].filter(Boolean).join(' · ')}</small></span>
              {c.lowQuality && <span className="dot-warn" title={c.lowQuality} aria-label={`Lower quality: ${c.lowQuality}`} />}
              {c.type && <Chip tone={c.lowQuality ? 'warn' : 'neutral'}>{c.type}</Chip>}
              {c.n !== undefined && c.cited !== false && <span className="num">{c.n}</span>}
            </button>
          </li>
        ))}
      </ul>
      {sel && (
        <div className="passage">
          <div className="row">
            <b>{sel.cited !== false && sel.n !== undefined ? `Passage used for claim ${sel.n}` : 'Consulted source'}</b>
            <span className="sp" />
            {sel.relevance !== undefined && <Chip tone={sel.relevance < 0.6 ? 'warn' : 'ok'}>Relevance {sel.relevance.toFixed(2)}</Chip>}
          </div>
          {sel.excerpt && <div className="small"><mark>{sel.excerpt}</mark></div>}
          {sel.lowQuality && <div className="small" style={{ color: 'var(--cp-warning)' }}><TriangleAlert size={14} aria-hidden="true" /> Lower quality: {sel.lowQuality}</div>}
          {sel.url && <div className="row"><a className="btn sm" href={sel.url} target="_blank" rel="noreferrer noopener"><ExternalLink size={14} aria-hidden="true" /> Open page</a></div>}
        </div>
      )}
    </div>
  );
}
